/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment } from 'react'

import {
  EuiFlexGroup,
  EuiHorizontalRule,
  EuiIcon,
  EuiSpacer,
  EuiText,
  EuiTitle,
  useEuiTheme,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { DocLink } from '@modules/doclinks'

import type { FunctionComponent } from 'react'

const LimitedAccessTooltip: FunctionComponent<{ isApiKey: boolean }> = ({ isApiKey }) => {
  const { euiTheme } = useEuiTheme()
  return (
    <Fragment>
      <EuiTitle size='xxs'>
        <EuiFlexGroup direction='row' gutterSize='s' alignItems='center'>
          <EuiIcon type='warning' color='warning' />
          <FormattedMessage
            id='roles-assignments.api.access.selector.limited-access-title'
            defaultMessage='Limited access'
          />
        </EuiFlexGroup>
      </EuiTitle>
      <EuiHorizontalRule margin='s' />

      <EuiText size='s' color={euiTheme.colors.textParagraph}>
        {isApiKey ? (
          <FormattedMessage
            id='roles-assignments.api.access.selector.api-key-limited-access-title'
            defaultMessage='One or more selected roles depend on Elasticsearch and Kibana API access. Without it, key features will be unavailable.'
          />
        ) : (
          <FormattedMessage
            id='roles-assignments.api.access.selector.org-owner-limited-access-title'
            defaultMessage='One or more selected roles depend on Elasticsearch and Kibana access. Without it, key features will be unavailable.'
          />
        )}
      </EuiText>
      <EuiSpacer size='s' />
      <div onMouseDown={(e) => e.preventDefault()}>
        <FormattedMessage
          id='roles-assignments.api.access.selector.learn-more-link'
          defaultMessage='<docLink>Learn more</docLink>'
          values={{
            docLink: (content) => (
              <DocLink
                showExternalLinkIcon={true}
                link={
                  isApiKey
                    ? 'cloudOrganizationApiKeysAccessDocLink'
                    : 'cloudOrganizationUserRolesAccessDocLink'
                }
              >
                {content}
              </DocLink>
            ),
          }}
        />
      </div>
    </Fragment>
  )
}

export default LimitedAccessTooltip
